import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  Paper,
  Grid,
  Button,
  Chip,
  Pagination,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar
} from "@mui/material";
import { 
  Search, 
  Calendar, 
  Filter, 
  ChevronRight, 
  Layout, 
  Clock, 
  CheckCircle2,
  ArrowUpDown
} from "lucide-react";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  lime: "#8FC440",
  yellow: "#E3E24F",
  pearl: "#EEF3F4"
};

const ListaPedidos = () => {
  const [pedidos, setPedidos] = useState([]);
  const [pedidosFiltrados, setPedidosFiltrados] = useState([]);
  const [nombreFiltro, setNombreFiltro] = useState("");
  const [programaFiltro, setProgramaFiltro] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("Pendiente");
  const [ordenFecha, setOrdenFecha] = useState("asc");
  const [paginaActual, setPaginaActual] = useState(1);
  
  const navigate = useNavigate();
  const pedidosPorPagina = 8;

  useEffect(() => {
    const fetchPedidos = async () => {
      try {
        let response = await fetch(`${apiUrl}/Pedido`, {
          headers: {
            "Content-Type": "application/json",
            apiKey: localStorage.getItem("apiKey"),
          },
        });
        if (response.ok) {
          let data = await response.json();
          setPedidos(data);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchPedidos();
  }, []);

  useEffect(() => {
    let filtrados = [...pedidos];

    if (nombreFiltro) {
      filtrados = filtrados.filter((p) =>
        p.nombre.toLowerCase().includes(nombreFiltro.toLowerCase())
      );
    }
    if (programaFiltro) {
      filtrados = filtrados.filter((p) =>
        p.programa.nombre.toLowerCase().includes(programaFiltro.toLowerCase())
      );
    }
    if (estadoFiltro) {
      filtrados = filtrados.filter(
        (p) => p.estado.toLowerCase() === estadoFiltro.toLowerCase()
      );
    }

    filtrados.sort((a, b) => {
      const dateA = new Date(a.fechaLimite);
      const dateB = new Date(b.fechaLimite);
      return ordenFecha === "asc" ? dateA - dateB : dateB - dateA;
    });

    setPedidosFiltrados(filtrados);
    setPaginaActual(1);
  }, [nombreFiltro, programaFiltro, estadoFiltro, ordenFecha, pedidos]);

  const totalPages = Math.ceil(pedidosFiltrados.length / pedidosPorPagina);
  const pedidosPagidados = pedidosFiltrados.slice(
    (paginaActual - 1) * pedidosPorPagina,
    paginaActual * pedidosPorPagina
  );

  return (
    <Box sx={{ p: { xs: 2, md: 6 }, pt: { xs: 10, md: 14 }, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      
      {/* HEADER DINÁMICO */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="overline" sx={{ color: PALETTE.teal, fontWeight: 800, letterSpacing: 2 }}>
          SOLICITUDES GLOBALES
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 900, color: PALETTE.dark }}>
          Pedidos a Comunicación
        </Typography>
      </Box>

      {/* BARRA DE FILTROS ESTILO SOFT */}
      <Paper elevation={0} sx={{ 
        p: 2, mb: 4, borderRadius: '24px', 
        backgroundColor: PALETTE.pearl, 
        border: `1px solid ${PALETTE.sky}` 
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nombre..."
              value={nombreFiltro}
              onChange={(e) => setNombreFiltro(e.target.value)}
              InputProps={{
                startAdornment: <Search size={18} style={{ marginRight: 8, color: PALETTE.teal }} />,
                sx: { borderRadius: '14px', backgroundColor: 'white' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Filtrar programa..."
              value={programaFiltro}
              onChange={(e) => setProgramaFiltro(e.target.value)}
              InputProps={{
                startAdornment: <Layout size={18} style={{ marginRight: 8, color: PALETTE.teal }} />,
                sx: { borderRadius: '14px', backgroundColor: 'white' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              size="small"
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value)}
              sx={{ borderRadius: '14px', backgroundColor: 'white' }}
              displayEmpty
            >
              <MenuItem value="">Todos los estados</MenuItem>
              <MenuItem value="Pendiente">Pendiente</MenuItem>
              <MenuItem value="Terminado">Terminado</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              size="small"
              value={ordenFecha}
              onChange={(e) => setOrdenFecha(e.target.value)}
              sx={{ borderRadius: '14px', backgroundColor: 'white' }}
              startAdornment={<ArrowUpDown size={18} style={{ marginRight: 8, color: PALETTE.teal }} />}
            >
              <MenuItem value="asc">Más antiguos primero</MenuItem>
              <MenuItem value="desc">Más recientes primero</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Paper>

      {/* TABLA ESTILIZADA */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '28px', border: '1px solid #eee' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: '#fafafa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>PROGRAMA</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>PEDIDO</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>FECHA LÍMITE</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>ESTADO</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pedidosPagidados.map((p) => (
              <TableRow key={p.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PALETTE.dark }}>
                    {p.programa.nombre}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{p.nombre}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Calendar size={14} color={PALETTE.teal} />
                    <Typography variant="body2">
                      {new Date(p.fechaLimite).toLocaleDateString("es-ES")}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={p.estado === "Terminado" ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                    label={p.estado}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      borderRadius: '8px',
                      bgcolor: p.estado === "Terminado" ? `${PALETTE.lime}20` : `${PALETTE.yellow}30`,
                      color: p.estado === "Terminado" ? PALETTE.lime : "#9A8D00",
                      border: `1px solid ${p.estado === "Terminado" ? PALETTE.lime : PALETTE.yellow}`,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TableCell align="right">
                  <IconButton 
                    onClick={() => navigate("/pedido/" + p.id)}
                    sx={{ color: PALETTE.dark, '&:hover': { bgcolor: PALETTE.ice, color: PALETTE.teal } }}
                  >
                    <ChevronRight size={22} />
                  </IconButton>
                </TableCell>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {pedidosPagidados.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
              No se encontraron pedidos que coincidan con los filtros.
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* PAGINACIÓN SOFT */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Pagination 
          count={totalPages} 
          page={paginaActual} 
          onChange={(e, value) => setPaginaActual(value)}
          sx={{
            '& .MuiPaginationItem-root': { borderRadius: '10px', fontWeight: 700 },
            '& .Mui-selected': { bgcolor: `${PALETTE.teal} !important`, color: 'white' }
          }}
        />
      </Box>
    </Box>
  );
};

export default ListaPedidos;